import React, { useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { brand } from "../mock";
import { Trash2, CalendarDays, ShoppingBag, Tag, Coins, Sparkles, Smartphone, CreditCard, Wallet, CheckCircle2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cities = ["Gurugram", "Sector 15 Gurugram", "DLF Phase 1-5", "Sushant Lok", "Cyber City", "Golf Course Road", "Sohna Road"];
const MIN_ORDER = 899;
const timeSlots = ["09:00 AM","10:00 AM","11:00 AM","12:00 PM","01:00 PM","02:00 PM","03:00 PM","04:00 PM","05:00 PM","06:00 PM","07:00 PM","08:00 PM"];

const slotToMinutes = (s) => {
  const [time, mer] = s.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

const CartDrawer = () => {
  const { items, removeItem, clear, open, setOpen, subtotal } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [checkout, setCheckout] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "Gurugram", time: "", address: "" });
  const [date, setDate] = useState();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [redeem, setRedeem] = useState(0);
  const [payment, setPayment] = useState("pay_at_service");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const coins = user?.coins || 0;
  const afterOffer = Math.max(0, subtotal - discount);
  const maxRedeem = Math.min(coins, Math.floor(afterOffer));
  const total = Math.max(0, afterOffer - Math.min(redeem, maxRedeem));

  const now = new Date();
  const isToday = date && format(date, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");
  const minMinutes = now.getHours() * 60 + now.getMinutes() + 60;
  const availableSlots = isToday ? timeSlots.filter((t) => slotToMinutes(t) >= minMinutes) : timeSlots;

  const handleDateSelect = (d) => {
    setDate(d);
    if (d) {
      const today = format(d, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
      if (today && form.time && slotToMinutes(form.time) < minMinutes) {
        setForm((f) => ({ ...f, time: "" }));
      }
    }
  };

  const applyCode = async () => {
    if (!code) return;
    try {
      const res = await axios.post(`${API}/offers/validate`, { code, subtotal });
      setDiscount(res.data.discount);
      toast({ title: `Coupon applied!`, description: `${res.data.title} — you saved ₹${res.data.discount}` });
    } catch { setDiscount(0); toast({ title: "Invalid or expired code" }); }
  };

  const startCheckout = () => {
    if (items.length === 0) { toast({ title: "Your cart is empty" }); return; }
    if (subtotal < MIN_ORDER) { toast({ title: `Minimum amount should be ₹${MIN_ORDER}`, description: `Please add ₹${MIN_ORDER - subtotal} more worth of services to proceed.` }); return; }
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
    setCheckout(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !date || !form.time) { toast({ title: "Please fill name, phone, date & time" }); return; }
    if (subtotal < MIN_ORDER) { toast({ title: `Minimum amount should be ₹${MIN_ORDER}` }); return; }
    if (isToday && slotToMinutes(form.time) < minMinutes) { toast({ title: "Please pick a slot at least 1 hour from now" }); setForm((f) => ({ ...f, time: "" })); return; }
    setSubmitting(true);
    try {
      const bookingData = {
        customer_name: form.name, phone: form.phone, email: form.email, city: form.city,
        address: form.address, date: format(date, "yyyy-MM-dd"), time: form.time,
        items, offer_code: discount > 0 ? code : "", coins_redeemed: Math.min(redeem, maxRedeem),
        payment_method: payment,
      };
      await axios.post(`${API}/bookings`, bookingData, { withCredentials: true });
      const summary = `Hi BeBlushify! 💖 I've booked: ${items.map((i) => i.name).join(", ")} on ${format(date, "PPP")} at ${form.time}. Name: ${form.name}, Phone: ${form.phone}, Location: ${form.city}${form.address ? ", " + form.address : ""}. Total: ₹${total} (${payment.replace(/_/g, " ")}). Please confirm.`;
      setDone({ summary, total });
      toast({ title: "Booking confirmed! 🎉", description: `We'll call ${form.phone} to confirm.` });
      clear();
      setForm({ name: "", phone: "", email: "", city: "Gurugram", time: "", address: "" });
      setDate(undefined); setCode(""); setDiscount(0); setRedeem(0); setCheckout(false);
    } catch { toast({ title: "Booking failed", description: "Please try again." }); } finally { setSubmitting(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-5 border-b">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="w-5 h-5 text-primary" /> {done ? "Booking Confirmed" : checkout ? "Checkout" : "Your Cart"} {!done && `(${items.length})`}
          </SheetTitle>
        </SheetHeader>

        {done ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center"><CheckCircle2 className="w-9 h-9" /></div>
            <h3 className="font-display text-2xl font-bold text-gray-900 mt-4">Thank you! 🎉</h3>
            <p className="text-gray-600 mt-2 text-sm">Your booking is placed. Our team will call you shortly to confirm. Send yourself a confirmation on WhatsApp below.</p>
            <a href={`https://wa.me/${brand.phoneRaw}?text=${encodeURIComponent(done.summary)}`} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full px-6 h-11">
              <MessageCircle className="w-5 h-5" /> Get Confirmation on WhatsApp
            </a>
            <button onClick={() => { setDone(null); setOpen(false); }} className="mt-4 text-sm text-gray-400 hover:text-primary">Close</button>
          </div>
        ) : (
        <>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <div className="text-center text-gray-400 py-16"><ShoppingBag className="w-10 h-10 mx-auto opacity-40" /><p className="mt-3">Your cart is empty. Add some services!</p></div>
          ) : (
            <div className="space-y-2">
              {items.map((i) => (
                <div key={i.name} className="flex items-center justify-between bg-secondary/50 rounded-xl px-4 py-3">
                  <div><div className="font-medium text-gray-900 text-sm">{i.name}</div><div className="text-xs text-gray-500 capitalize">{i.type}</div></div>
                  <div className="flex items-center gap-3"><span className="font-bold text-primary">₹{i.price}</span>
                    <button onClick={() => removeItem(i.name)} data-testid={`checkout-remove-${i.name}`} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>
                </div>
              ))}
            </div>
          )}

          {checkout && items.length > 0 && (
            <form id="checkout-form" onSubmit={submit} className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>City</Label>
                  <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Time Slot *</Label>
                  <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}><SelectTrigger data-testid="time-slot-trigger"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{availableSlots.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground" data-testid="no-slots-msg">No slots left today — pick another date</div> : availableSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="space-y-1.5"><Label>Preferred Date *</Label>
                <Popover><PopoverTrigger asChild><Button type="button" variant="outline" className="w-full justify-start font-normal"><CalendarDays className="mr-2 h-4 w-4" />{date ? format(date, "PPP") : "Pick a date"}</Button></PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={date} onSelect={handleDateSelect} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus /></PopoverContent></Popover></div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House / Flat, Sector, Landmark" /></div>
              <div className="space-y-1.5"><Label>Payment Method</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ id: "upi", label: "UPI", icon: Smartphone }, { id: "card", label: "Card", icon: CreditCard }, { id: "pay_at_service", label: "Pay at Service", icon: Wallet }].map((p) => (
                    <button type="button" key={p.id} onClick={() => setPayment(p.id)} className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-xs font-medium transition-colors ${payment === p.id ? "border-primary bg-primary/5 text-primary" : "text-gray-600 hover:border-primary/40"}`}>
                      <p.icon className="w-4 h-4" /> {p.label}
                    </button>
                  ))}
                </div>
                {(payment === "upi" || payment === "card") && <p className="text-[11px] text-gray-400">Online payment is in demo mode — you can pay at service for now.</p>}
              </div>
            </form>
          )}

          {items.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex gap-2"><div className="flex-1 flex items-center gap-2 border rounded-lg px-3"><Tag className="w-4 h-4 text-primary" /><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="border-0 shadow-none focus-visible:ring-0 px-0 h-9" /></div><Button type="button" variant="outline" onClick={applyCode}>Apply</Button></div>
              {user && coins > 0 && (
                <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-sm text-amber-700"><Coins className="w-4 h-4" /> Redeem coins (max {maxRedeem})</div>
                  <Input type="number" value={redeem} min={0} max={maxRedeem} onChange={(e) => setRedeem(Math.max(0, Math.min(maxRedeem, Number(e.target.value))))} className="w-20 h-8" />
                </div>
              )}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-5 space-y-3">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>₹{subtotal}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-600"><span>Offer ({code})</span><span>-₹{discount}</span></div>}
              {redeem > 0 && <div className="flex justify-between text-amber-600"><span>Coins</span><span>-₹{Math.min(redeem, maxRedeem)}</span></div>}
              <div className="flex justify-between font-bold text-lg text-gray-900 pt-1"><span>Total</span><span>₹{total}</span></div>
              {user && <div className="flex items-center gap-1 text-xs text-amber-600"><Sparkles className="w-3 h-3" /> You'll earn {Math.floor(total/100)*10} coins</div>}
            </div>
            {!checkout ? (
              <>
                {subtotal < MIN_ORDER && <p className="text-xs text-center text-red-500" data-testid="min-order-warning">Minimum amount should be ₹{MIN_ORDER}. Add ₹{MIN_ORDER - subtotal} more to proceed.</p>}
                <Button onClick={startCheckout} data-testid="proceed-checkout-btn" className="w-full bg-primary hover:bg-primary/90 h-11">Proceed to Checkout</Button>
              </>
            ) : (
              <Button type="submit" form="checkout-form" disabled={submitting} className="w-full bg-primary hover:bg-primary/90 h-11">{submitting ? "Booking..." : `Confirm Booking · ₹${total}`}</Button>
            )}
            {!user && <p className="text-xs text-center text-gray-400">Login to earn loyalty coins on every booking</p>}
          </div>
        )}
        </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
